import AddMeetingModal from '@/components/features/Companies/forms/AddMeetingModal';
import AddPhoneCallModal from '@/components/features/Companies/forms/AddPhoneCallModal';
import { GenericToolbar, type ToolbarViewMode } from '@/components/ui/generic-toolbar/GenericToolbar';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/shadcn/alert-dialog';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent } from '@/components/ui/shadcn/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { Company, User as InterfaceUser, People } from '@/types';
import type { EditableMeeting, EditablePhoneCall } from '@/types/forms/companies';
import { router } from '@inertiajs/react';
import { differenceInDays, format } from 'date-fns';
import { CalendarDays, ChevronDown, Pencil, PhoneCall as PhoneCallIcon, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

export interface PhoneCall {
    id: string;
    description?: string | null;
    call_date: string;
    call_time?: string | null;
    call_direction?: string | null;
    call_result?: string | null;
    duration_minutes?: number | null;
    follow_up_date?: string | null;
    follow_up_note?: string | null;
    created_at: string;
    created_by?: InterfaceUser;
    createdBy?: InterfaceUser;
    participants?: People[];
}

export interface Meeting {
    id: string;
    description?: string | null;
    meeting_date: string;
    meeting_time?: string | null;
    meeting_format?: string | null;
    location?: string | null;
    follow_up_date?: string | null;
    follow_up_note?: string | null;
    created_at: string;
    created_by?: InterfaceUser;
    createdBy?: InterfaceUser;
    participants?: People[];
}

interface CompanyInteractionsProps {
    phoneCalls: PhoneCall[];
    meetings: Meeting[];
    company?: Company | null;
    people?: People[];
    /** Hide the built-in summary cards when the host page renders them itself via useInteractionSummary(). */
    showSummary?: boolean;
}

type ActivityType = 'call' | 'meeting';
type DateRange = '30d' | '90d' | 'year' | 'all';

interface ActivityRow {
    id: string;
    type: ActivityType;
    notes: string;
    activityDate: string;
    activityTime: string | null;
    createdAt: string;
    createdBy?: InterfaceUser;
    participants: People[];
    callDirection?: string | null;
    callResult?: string | null;
    durationMinutes?: number | null;
    meetingFormat?: string | null;
    location?: string | null;
    followUpDate?: string | null;
    followUpNote?: string | null;
}

function fallbackUserLabel(user?: InterfaceUser): string {
    if (!user) {
        return '—';
    }

    return `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || '—';
}

function initials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
        return '—';
    }

    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
    }

    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function formatActivityDateOnly(value?: string | null): string {
    if (!value) {
        return '—';
    }

    const isoDate = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim());
    if (isoDate) {
        return isoDate[1];
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return format(parsed, 'yyyy-MM-dd');
}

/** Calls and meetings folded into one list, newest first. */
function buildActivityRows(phoneCalls: PhoneCall[], meetings: Meeting[]): ActivityRow[] {
    const callRows = phoneCalls.map((call) => ({
        id: call.id,
        type: 'call' as const,
        notes: call.description ?? '',
        activityDate: call.call_date,
        activityTime: call.call_time ?? null,
        createdAt: call.created_at,
        createdBy: call.created_by ?? call.createdBy,
        participants: call.participants ?? [],
        callDirection: call.call_direction ?? null,
        callResult: call.call_result ?? null,
        durationMinutes: call.duration_minutes ?? null,
        meetingFormat: null,
        location: null,
        followUpDate: call.follow_up_date ?? null,
        followUpNote: call.follow_up_note ?? null,
    }));

    const meetingRows = meetings.map((meeting) => ({
        id: meeting.id,
        type: 'meeting' as const,
        notes: meeting.description ?? '',
        activityDate: meeting.meeting_date,
        activityTime: meeting.meeting_time ?? null,
        createdAt: meeting.created_at,
        createdBy: meeting.created_by ?? meeting.createdBy,
        participants: meeting.participants ?? [],
        callDirection: null,
        callResult: null,
        durationMinutes: null,
        meetingFormat: meeting.meeting_format ?? null,
        location: meeting.location ?? null,
        followUpDate: meeting.follow_up_date ?? null,
        followUpNote: meeting.follow_up_note ?? null,
    }));

    return [...callRows, ...meetingRows].sort((a, b) => {
        const aKey = `${a.activityDate} ${a.activityTime ?? '00:00'} ${a.createdAt}`;
        const bKey = `${b.activityDate} ${b.activityTime ?? '00:00'} ${b.createdAt}`;
        return bKey.localeCompare(aKey);
    });
}

/**
 * The engagement headline for a set of interactions — last activity, last call,
 * last meeting and the warm/cold status. Exported so a page can render the same
 * figures in its own layout instead of the built-in summary cards.
 */
export function useInteractionSummary(phoneCalls: PhoneCall[], meetings: Meeting[]) {
    const { t } = useTranslations();

    return useMemo(() => {
        const rows = buildActivityRows(phoneCalls, meetings);
        const lastActivity = rows[0];
        const lastCall = rows.find((row) => row.type === 'call');
        const lastMeeting = rows.find((row) => row.type === 'meeting');

        const formatActivityDate = (row?: ActivityRow) => {
            if (!row) {
                return t('companies.interactions.summary.none', 'Nėra veiklų');
            }

            return format(new Date(row.activityDate), 'yyyy.MM.dd');
        };

        const status = (() => {
            if (!lastActivity) {
                return {
                    key: 'none',
                    label: t('companies.interactions.summary.status.none', 'Nėra veiklų'),
                    hint: t('companies.interactions.summary.status.none_hint', 'Nėra veiklų'),
                    className: 'bg-muted text-muted-foreground',
                };
            }

            const diff = differenceInDays(new Date(), new Date(lastActivity.activityDate));
            if (diff <= 14) {
                return {
                    key: 'active',
                    label: t('companies.interactions.summary.status.active', 'Aktyvus'),
                    hint: t('companies.interactions.summary.status.active_hint', 'Aktyvus (0–14 d.)'),
                    className: 'bg-success-subtle text-success-subtle-foreground',
                };
            }

            if (diff <= 30) {
                return {
                    key: 'cooling',
                    label: t('companies.interactions.summary.status.cooling', 'Vėstantis'),
                    hint: t('companies.interactions.summary.status.cooling_hint', 'Vėstantis (15–30 d.)'),
                    className: 'bg-warning-subtle text-warning-subtle-foreground',
                };
            }

            return {
                key: 'cold',
                label: t('companies.interactions.summary.status.cold', 'Šaltas'),
                hint: t('companies.interactions.summary.status.cold_hint', 'Šaltas (>30 d.)'),
                className: 'bg-muted text-muted-foreground',
            };
        })();

        return {
            lastActivity: formatActivityDate(lastActivity),
            lastCall: formatActivityDate(lastCall),
            lastMeeting: formatActivityDate(lastMeeting),
            status,
        };
    }, [meetings, phoneCalls, t]);
}

export default function CompanyInteractions({ phoneCalls, meetings, company, people = [], showSummary = true }: CompanyInteractionsProps) {
    const { t } = useTranslations();
    const [typeFilter, setTypeFilter] = useState<'all' | ActivityType>('all');
    const [contactFilter, setContactFilter] = useState<'all' | string>('all');
    const [dateRange, setDateRange] = useState<DateRange>('90d');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());

    // Toolbar state (matches the leads index layout).
    const isMobile = useIsMobile();
    const [viewMode, setViewMode] = useState<ToolbarViewMode>(isMobile ? 'grid' : 'table');
    const [perPage, setPerPage] = useState(10);
    const [columnFilterSearch, setColumnFilterSearch] = useState<Record<string, string>>({});
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isSortOpen, setIsSortOpen] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set(['type', 'contact', 'notes', 'date_time', 'created_by']));

    useEffect(() => {
        setViewMode((current) => (isMobile && current === 'table' ? 'grid' : current));
    }, [isMobile]);

    // Edit/delete are only available in a company context (the routes are
    // company-scoped); e.g. the person page renders this list read-only.
    const canManage = Boolean(company);
    const [editingMeeting, setEditingMeeting] = useState<EditableMeeting | null>(null);
    const [editingCall, setEditingCall] = useState<EditablePhoneCall | null>(null);
    const [deleting, setDeleting] = useState<ActivityRow | null>(null);

    const startEdit = (row: ActivityRow) => {
        const participantIds = row.participants.map((person) => String(person.id));
        if (row.type === 'meeting') {
            setEditingMeeting({
                id: row.id,
                description: row.notes,
                meeting_date: row.activityDate,
                meeting_time: row.activityTime,
                meeting_format: row.meetingFormat,
                location: row.location,
                follow_up_date: row.followUpDate,
                follow_up_note: row.followUpNote,
                participantIds,
            });
        } else {
            setEditingCall({
                id: row.id,
                description: row.notes,
                call_date: row.activityDate,
                call_time: row.activityTime,
                call_direction: row.callDirection,
                call_result: row.callResult,
                duration_minutes: row.durationMinutes,
                follow_up_date: row.followUpDate,
                follow_up_note: row.followUpNote,
                participantIds,
            });
        }
    };

    const confirmDelete = () => {
        if (!deleting || !company) {
            return;
        }

        const routeName = deleting.type === 'meeting' ? 'company.meeting.destroy' : 'company.phone-call.destroy';
        router.delete(route(routeName, [company.id, deleting.id]), {
            preserveScroll: true,
            onFinish: () => setDeleting(null),
        });
    };

    const rows = useMemo<ActivityRow[]>(() => buildActivityRows(phoneCalls, meetings), [meetings, phoneCalls]);

    const contactOptions = useMemo(() => {
        const map = new Map<string, string>();
        rows.forEach((row) => {
            row.participants.forEach((person) => {
                const label = `${person.first_name ?? ''} ${person.last_name ?? ''}`.trim();
                if (label) {
                    map.set(person.id, label);
                }
            });
        });

        return Array.from(map.entries()).map(([id, label]) => ({ id, label }));
    }, [rows]);

    const filteredRows = useMemo(() => {
        const now = new Date();
        const q = search.trim().toLowerCase();

        const byDateRange = (row: ActivityRow): boolean => {
            if (dateRange === 'all') {
                return true;
            }

            const date = new Date(row.activityDate);
            if (Number.isNaN(date.getTime())) {
                return false;
            }

            const days = differenceInDays(now, date);
            if (dateRange === '30d') {
                return days <= 30;
            }
            if (dateRange === '90d') {
                return days <= 90;
            }

            return date.getFullYear() === now.getFullYear();
        };

        return rows.filter((row) => {
            if (typeFilter !== 'all' && row.type !== typeFilter) {
                return false;
            }

            if (contactFilter !== 'all') {
                const hasContact = row.participants.some((person) => person.id === contactFilter);
                if (!hasContact) {
                    return false;
                }
            }

            if (!byDateRange(row)) {
                return false;
            }

            if (q.length === 0) {
                return true;
            }

            const participantsText = row.participants.map((person) => `${person.first_name ?? ''} ${person.last_name ?? ''}`.trim()).join(' ');
            const creatorText = fallbackUserLabel(row.createdBy);
            return `${row.notes} ${participantsText} ${creatorText}`.toLowerCase().includes(q);
        });
    }, [contactFilter, dateRange, rows, search, typeFilter]);

    const summary = useInteractionSummary(phoneCalls, meetings);

    const totalPages = Math.max(1, Math.ceil(filteredRows.length / perPage));
    const currentPage = Math.min(page, totalPages);
    const paginatedRows = filteredRows.slice((currentPage - 1) * perPage, currentPage * perPage);

    const from = filteredRows.length === 0 ? 0 : (currentPage - 1) * perPage + 1;
    const to = Math.min(currentPage * perPage, filteredRows.length);

    const renderContact = (row: ActivityRow) => {
        const person = row.participants[0];
        if (!person) {
            return <span className="text-muted-foreground text-sm">{t('companies.interactions.table.no_contact', 'Nenurodytas kontaktas')}</span>;
        }

        const name = `${person.first_name ?? ''} ${person.last_name ?? ''}`.trim();
        return (
            <div className="flex items-center gap-2">
                <span className="bg-muted text-muted-foreground inline-flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold">
                    {initials(name)}
                </span>
                <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{name}</p>
                    <p className="text-muted-foreground truncate text-xs">{person.position || '—'}</p>
                </div>
            </div>
        );
    };

    const callDirectionLabel = (value?: string | null): string => {
        if (!value) {
            return '—';
        }

        if (value === 'outbound') {
            return t('companies.forms.add_phone_call.options.outbound', 'Išeinantis');
        }

        if (value === 'inbound') {
            return t('companies.forms.add_phone_call.options.inbound', 'Įeinantis');
        }

        return value;
    };

    const callResultLabel = (value?: string | null): string => {
        if (!value) {
            return '—';
        }

        if (value === 'connected') {
            return t('companies.forms.add_phone_call.options.connected', 'Pavyko susisiekti');
        }

        if (value === 'no_answer') {
            return t('companies.forms.add_phone_call.options.no_answer', 'Neatsiliepė');
        }

        if (value === 'callback_requested') {
            return t('companies.forms.add_phone_call.options.callback_requested', 'Paprašė perskambinti');
        }

        if (value === 'wrong_contact') {
            return t('companies.forms.add_phone_call.options.wrong_contact', 'Netinkamas kontaktas');
        }

        return value;
    };

    const meetingFormatLabel = (value?: string | null): string => {
        if (!value) {
            return '—';
        }

        if (value === 'in_person') {
            return t('companies.forms.add_meeting.options.in_person', 'Gyvai');
        }

        if (value === 'online') {
            return t('companies.forms.add_meeting.options.online', 'Online');
        }

        if (value === 'customer_visited') {
            return t('companies.forms.add_meeting.options.customer_visited', 'Klientas atvyko');
        }

        return value;
    };

    if (rows.length === 0) {
        return (
            <Card>
                <CardContent className="py-12 text-center">
                    <p className="text-lg font-medium">{t('companies.interactions.empty.title', 'Veiklų dar nėra')}</p>
                    <p className="text-muted-foreground mt-1 text-sm">
                        {t('companies.interactions.empty.description', 'Užregistruokite pirmą skambutį arba susitikimą su šiuo klientu.')}
                    </p>
                </CardContent>
            </Card>
        );
    }

    const columnDefinitions = [
        { id: 'type', label: t('companies.interactions.table.type', 'Tipas') },
        { id: 'contact', label: t('companies.interactions.table.contact', 'Kontaktinis asmuo') },
        { id: 'notes', label: t('companies.interactions.table.notes', 'Užrašai') },
        { id: 'date_time', label: t('companies.interactions.table.date_time', 'Data ir laikas') },
        { id: 'created_by', label: t('companies.interactions.table.created_by', 'Užregistravo') },
    ];

    const quickFilterDefinitions = [
        { id: 'type', label: t('companies.interactions.table.type', 'Tipas') },
        { id: 'contact', label: t('companies.interactions.table.contact', 'Kontaktinis asmuo') },
    ];

    const columnFilterOptions: Record<string, Array<{ value: string; label: string }>> = {
        type: [
            { value: 'call', label: t('companies.interactions.filters.calls', 'Skambučiai') },
            { value: 'meeting', label: t('companies.interactions.filters.meetings', 'Susitikimai') },
        ],
        contact: contactOptions.map((option) => ({ value: option.id, label: option.label })),
    };

    const columnFilters: Record<string, string[]> = {
        type: typeFilter === 'all' ? [] : [typeFilter],
        contact: contactFilter === 'all' ? [] : [contactFilter],
    };

    const handleFilterChange = (column: string, values: string[]) => {
        if (column === 'type') {
            setTypeFilter(values.length ? (values[0] as ActivityType) : 'all');
        } else if (column === 'contact') {
            setContactFilter(values.length ? values[0] : 'all');
        }
        setPage(1);
    };

    const toggleSingleFilter = (column: string, value: string) => {
        const current = columnFilters[column] ?? [];
        handleFilterChange(column, current.includes(value) ? [] : [value]);
    };

    const clearFilters = () => {
        setTypeFilter('all');
        setContactFilter('all');
        setDateRange('90d');
        setSearch('');
        setPage(1);
    };

    const handleColumnShow = (column: string) => setVisibleColumns((prev) => new Set(prev).add(column));
    const handleColumnHide = (column: string) =>
        setVisibleColumns((prev) => {
            const next = new Set(prev);
            next.delete(column);
            return next;
        });

    const quickFilterNames = quickFilterDefinitions.filter((q) => (columnFilters[q.id] ?? []).length > 0).map((q) => q.label);
    const quickFilterButtonLabel =
        quickFilterNames.length > 0
            ? `${t('companies.interactions.toolbar.quick_filters', 'Filtrai')}: ${quickFilterNames.join(', ')}`
            : t('companies.interactions.toolbar.all_activities', 'Visos veiklos');

    return (
        <div className="space-y-4">
            <div className={cn('grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4', !showSummary && 'hidden')}>
                <Card>
                    <CardContent className="pt-5">
                        <p className="text-muted-foreground text-sm">{t('companies.interactions.summary.last_activity', 'Paskutinė veikla')}</p>
                        <p className="text-2xl font-semibold">{summary.lastActivity}</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-5">
                        <p className="text-muted-foreground text-sm">{t('companies.interactions.summary.last_call', 'Paskutinis skambutis')}</p>
                        <p className="text-2xl font-semibold">{summary.lastCall}</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-5">
                        <p className="text-muted-foreground text-sm">{t('companies.interactions.summary.last_meeting', 'Paskutinis susitikimas')}</p>
                        <p className="text-2xl font-semibold">{summary.lastMeeting}</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-5">
                        <p className="text-muted-foreground text-sm">{t('companies.interactions.summary.status_label', 'Būsena')}</p>
                        <Badge className={cn('mt-1', summary.status.className)}>{summary.status.label}</Badge>
                        <p className="text-muted-foreground mt-2 text-sm">{summary.status.hint}</p>
                    </CardContent>
                </Card>
            </div>

            <GenericToolbar
                quickFilterButtonLabel={quickFilterButtonLabel}
                quickFilterDefinitions={quickFilterDefinitions}
                columnDefinitions={columnDefinitions}
                columnFilterOptions={columnFilterOptions}
                columnFilters={columnFilters}
                columnFilterSearch={columnFilterSearch}
                setColumnFilterSearch={setColumnFilterSearch}
                toggleSingleFilter={toggleSingleFilter}
                handleFilterChange={handleFilterChange}
                clearFilters={clearFilters}
                isFilterOpen={isFilterOpen}
                setIsFilterOpen={setIsFilterOpen}
                isSortOpen={isSortOpen}
                setIsSortOpen={setIsSortOpen}
                sortColumn={null}
                sortDirection={null}
                handleSortChange={() => {}}
                clearSort={() => {}}
                search={search}
                setSearch={(value) => {
                    setSearch(value);
                    setPage(1);
                }}
                perPage={perPage}
                onPerPageChange={(value) => {
                    setPerPage(value);
                    setPage(1);
                }}
                viewMode={viewMode}
                setViewMode={setViewMode}
                visibleColumns={visibleColumns}
                handleColumnShow={handleColumnShow}
                handleColumnHide={handleColumnHide}
                filteredCount={filteredRows.length}
                resourceName="veikla"
                resourceNamePlural={t('companies.interactions.resource_plural', 'veiklų')}
                sortableColumns={[]}
                excludeFromMoreFilters={['type', 'contact']}
                customFilters={
                    <div className="space-y-2">
                        <span className="text-sm font-medium">{t('companies.interactions.filters.date_range', 'Laikotarpis')}</span>
                        <Select
                            value={dateRange}
                            onValueChange={(value: DateRange) => {
                                setDateRange(value);
                                setPage(1);
                            }}
                        >
                            <SelectTrigger className="w-full">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="30d">{t('companies.interactions.filters.last_30_days', 'Paskutinės 30 d.')}</SelectItem>
                                <SelectItem value="90d">{t('companies.interactions.filters.last_90_days', 'Paskutinės 90 d.')}</SelectItem>
                                <SelectItem value="year">{t('companies.interactions.filters.this_year', 'Šie metai')}</SelectItem>
                                <SelectItem value="all">{t('companies.interactions.filters.all_time', 'Visas laikotarpis')}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                }
            />

            <div className={cn('bg-card overflow-x-auto rounded-xl border', viewMode !== 'table' && 'hidden')}>
                <Table>
                    <TableHeader>
                        <TableRow>
                            {visibleColumns.has('type') && <TableHead>{t('companies.interactions.table.type', 'Tipas')}</TableHead>}
                            {visibleColumns.has('contact') && <TableHead>{t('companies.interactions.table.contact', 'Kontaktinis asmuo')}</TableHead>}
                            {visibleColumns.has('notes') && <TableHead>{t('companies.interactions.table.notes', 'Užrašai')}</TableHead>}
                            {visibleColumns.has('date_time') && (
                                <TableHead>{t('companies.interactions.table.date_time', 'Data ir laikas')}</TableHead>
                            )}
                            {visibleColumns.has('created_by') && (
                                <TableHead>{t('companies.interactions.table.created_by', 'Užregistravo')}</TableHead>
                            )}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {paginatedRows.map((row) => {
                            const rowKey = `${row.type}-${row.id}`;
                            const isExpanded = expandedRowIds.has(rowKey);
                            const createdByName = fallbackUserLabel(row.createdBy);
                            const createdDate = new Date(row.activityDate);
                            const notePreview = row.notes.length > 120 ? `${row.notes.slice(0, 120)}…` : row.notes;
                            const contact = row.participants[0];
                            const contactName = contact
                                ? `${contact.first_name ?? ''} ${contact.last_name ?? ''}`.trim()
                                : t('companies.interactions.table.no_contact', 'Nenurodytas kontaktas');

                            return [
                                <TableRow
                                    key={rowKey}
                                    className="cursor-pointer"
                                    onClick={() => {
                                        setExpandedRowIds((previous) => {
                                            const next = new Set(previous);
                                            if (next.has(rowKey)) {
                                                next.delete(rowKey);
                                            } else {
                                                next.add(rowKey);
                                            }
                                            return next;
                                        });
                                    }}
                                >
                                    {visibleColumns.has('type') && (
                                        <TableCell>
                                            <div className="inline-flex items-center gap-2">
                                                <ChevronDown className={cn('h-4 w-4 transition-transform', isExpanded && 'rotate-180')} />
                                                <span
                                                    className={cn(
                                                        'inline-flex h-8 w-8 items-center justify-center rounded-lg',
                                                        row.type === 'call'
                                                            ? 'bg-success-subtle text-success-subtle-foreground'
                                                            : 'bg-info-subtle text-info-subtle-foreground',
                                                    )}
                                                >
                                                    {row.type === 'call' ? (
                                                        <PhoneCallIcon className="h-4 w-4" />
                                                    ) : (
                                                        <CalendarDays className="h-4 w-4" />
                                                    )}
                                                </span>
                                                <Badge
                                                    variant="outline"
                                                    className={cn(
                                                        row.type === 'call' ? 'text-success-subtle-foreground' : 'text-info-subtle-foreground',
                                                    )}
                                                >
                                                    {row.type === 'call'
                                                        ? t('companies.interactions.badges.call', 'Skambutis')
                                                        : t('companies.interactions.badges.meeting', 'Susitikimas')}
                                                </Badge>
                                            </div>
                                        </TableCell>
                                    )}
                                    {visibleColumns.has('contact') && <TableCell>{renderContact(row)}</TableCell>}
                                    {visibleColumns.has('notes') && (
                                        <TableCell>
                                            <p className="line-clamp-2 max-w-[360px] text-sm">{notePreview || '—'}</p>
                                        </TableCell>
                                    )}
                                    {visibleColumns.has('date_time') && (
                                        <TableCell>
                                            <div className="text-sm">
                                                <p>{Number.isNaN(createdDate.getTime()) ? '—' : format(createdDate, 'yyyy.MM.dd')}</p>
                                                <p className="text-muted-foreground">{row.activityTime || '—'}</p>
                                            </div>
                                        </TableCell>
                                    )}
                                    {visibleColumns.has('created_by') && (
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <span className="bg-muted text-muted-foreground inline-flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold">
                                                    {initials(createdByName)}
                                                </span>
                                                <span className="text-sm">{createdByName}</span>
                                            </div>
                                        </TableCell>
                                    )}
                                </TableRow>,
                                isExpanded ? (
                                    <TableRow key={`${rowKey}-expanded`}>
                                        <TableCell colSpan={visibleColumns.size} className="bg-muted/20">
                                            <div className="grid grid-cols-3 gap-4 p-4 text-sm">
                                                <div className="space-y-2">
                                                    <p className="text-muted-foreground">
                                                        {t('companies.interactions.table.contact', 'Kontaktinis asmuo')}
                                                    </p>
                                                    <p>{contactName}</p>
                                                    <p className="text-muted-foreground">
                                                        {t('companies.interactions.table.date_time', 'Data ir laikas')}
                                                    </p>
                                                    <p>
                                                        {Number.isNaN(createdDate.getTime()) ? '—' : format(createdDate, 'yyyy.MM.dd')}{' '}
                                                        {row.activityTime || '—'}
                                                    </p>
                                                    {row.durationMinutes != null && (
                                                        <>
                                                            <p className="text-muted-foreground">
                                                                {t('companies.forms.add_phone_call.labels.duration_minutes', 'Trukmė (min.)')}
                                                            </p>
                                                            <p>{row.durationMinutes} min.</p>
                                                        </>
                                                    )}
                                                </div>
                                                <div className="space-y-2">
                                                    <p className="text-muted-foreground">{t('companies.interactions.table.notes', 'Užrašai')}</p>
                                                    <p className="whitespace-pre-wrap">{row.notes || '—'}</p>
                                                </div>
                                                <div className="space-y-2">
                                                    <p className="text-muted-foreground">
                                                        {t('companies.interactions.table.created_by', 'Užregistravo')}
                                                    </p>
                                                    <p>{createdByName}</p>
                                                    {row.callDirection && (
                                                        <>
                                                            <p className="text-muted-foreground">
                                                                {t('companies.forms.add_phone_call.labels.call_direction', 'Skambučio kryptis')}
                                                            </p>
                                                            <p>{callDirectionLabel(row.callDirection)}</p>
                                                        </>
                                                    )}
                                                    {row.callResult && (
                                                        <>
                                                            <p className="text-muted-foreground">
                                                                {t('companies.forms.add_phone_call.labels.call_result', 'Skambučio rezultatas')}
                                                            </p>
                                                            <p>{callResultLabel(row.callResult)}</p>
                                                        </>
                                                    )}
                                                    {row.meetingFormat && (
                                                        <>
                                                            <p className="text-muted-foreground">
                                                                {t('companies.forms.add_meeting.labels.meeting_format', 'Susitikimo tipas')}
                                                            </p>
                                                            <p>{meetingFormatLabel(row.meetingFormat)}</p>
                                                        </>
                                                    )}
                                                    {row.followUpDate && (
                                                        <>
                                                            <p className="text-muted-foreground">
                                                                {t('companies.forms.follow_up.labels.date', 'Tolimesnio veiksmo data')}
                                                            </p>
                                                            <p>{formatActivityDateOnly(row.followUpDate)}</p>
                                                        </>
                                                    )}
                                                    {row.followUpNote && (
                                                        <>
                                                            <p className="text-muted-foreground">
                                                                {t('companies.forms.follow_up.labels.note', 'Pastaba')}
                                                            </p>
                                                            <p className="whitespace-pre-wrap">{row.followUpNote}</p>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            {canManage ? (
                                                <div className="flex justify-end gap-2 px-4 pb-4">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            startEdit(row);
                                                        }}
                                                    >
                                                        <Pencil className="mr-2 h-4 w-4" />
                                                        {t('companies.interactions.actions.edit', 'Redaguoti')}
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            setDeleting(row);
                                                        }}
                                                    >
                                                        <Trash2 className="mr-2 h-4 w-4" />
                                                        {t('companies.interactions.actions.delete', 'Ištrinti')}
                                                    </Button>
                                                </div>
                                            ) : null}
                                        </TableCell>
                                    </TableRow>
                                ) : null,
                            ];
                        })}
                    </TableBody>
                </Table>
            </div>

            <div className={cn('grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3', viewMode !== 'grid' && 'hidden')}>
                {paginatedRows.map((row) => {
                    const createdByName = fallbackUserLabel(row.createdBy);
                    const contact = row.participants[0];
                    const contactName = contact
                        ? `${contact.first_name ?? ''} ${contact.last_name ?? ''}`.trim()
                        : t('companies.interactions.table.no_contact', 'Nenurodytas kontaktas');
                    return (
                        <Card key={`${row.type}-${row.id}`}>
                            <CardContent className="space-y-2 pt-4">
                                <p className="text-sm font-medium">
                                    {row.type === 'call'
                                        ? t('companies.interactions.badges.call', 'Skambutis')
                                        : t('companies.interactions.badges.meeting', 'Susitikimas')}
                                    {' · '}
                                    {format(new Date(row.activityDate), 'yyyy.MM.dd')}
                                    {' · '}
                                    {row.activityTime || '—'}
                                </p>
                                <p className="text-sm">{contactName}</p>
                                <p className="text-muted-foreground line-clamp-2 text-sm">{row.notes}</p>
                                <p className="text-muted-foreground text-xs">
                                    {t('companies.interactions.table.created_by', 'Užregistravo')}: {createdByName}
                                </p>
                                {canManage ? (
                                    <div className="flex gap-2 pt-1">
                                        <Button variant="outline" size="sm" onClick={() => startEdit(row)}>
                                            <Pencil className="mr-2 h-4 w-4" />
                                            {t('companies.interactions.actions.edit', 'Redaguoti')}
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={() => setDeleting(row)}>
                                            <Trash2 className="mr-2 h-4 w-4" />
                                            {t('companies.interactions.actions.delete', 'Ištrinti')}
                                        </Button>
                                    </div>
                                ) : null}
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            <div className="mt-4 flex flex-col gap-2 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-muted-foreground text-sm">
                    {t('companies.interactions.pagination.showing', 'Rodoma :from–:to iš :total įrašų')
                        .replace(':from', String(from))
                        .replace(':to', String(to))
                        .replace(':total', String(filteredRows.length))}
                </p>
                <div className="flex items-center gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                        disabled={currentPage <= 1}
                    >
                        ‹
                    </Button>
                    <span className="text-sm font-medium">{currentPage}</span>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                        disabled={currentPage >= totalPages}
                    >
                        ›
                    </Button>
                </div>
            </div>

            {company ? (
                <>
                    <AddMeetingModal
                        company={company}
                        people={people}
                        meeting={editingMeeting ?? undefined}
                        open={editingMeeting !== null}
                        onOpenChange={(isOpen) => {
                            if (!isOpen) {
                                setEditingMeeting(null);
                            }
                        }}
                    />
                    <AddPhoneCallModal
                        company={company}
                        users={people}
                        phoneCall={editingCall ?? undefined}
                        open={editingCall !== null}
                        onOpenChange={(isOpen) => {
                            if (!isOpen) {
                                setEditingCall(null);
                            }
                        }}
                    />
                    <AlertDialog
                        open={deleting !== null}
                        onOpenChange={(isOpen) => {
                            if (!isOpen) {
                                setDeleting(null);
                            }
                        }}
                    >
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>{t('companies.interactions.delete_dialog.title', 'Ištrinti šią veiklą?')}</AlertDialogTitle>
                                <AlertDialogDescription>
                                    {t(
                                        'companies.interactions.delete_dialog.description',
                                        'Tai visam laikui pašalins užregistruotą veiklą. Šio veiksmo atšaukti negalima.',
                                    )}
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>{t('companies.interactions.delete_dialog.cancel', 'Atšaukti')}</AlertDialogCancel>
                                <AlertDialogAction onClick={confirmDelete}>
                                    {t('companies.interactions.delete_dialog.confirm', 'Ištrinti')}
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </>
            ) : null}
        </div>
    );
}
